// Component for the file explorer in PMX activity details page.
// It contains the tree view for repositories (left side)
// and the table view for files (right side).

import { useState, useContext } from "react";
import { Button, Container, Row, Spinner } from "react-bootstrap";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import { useBrowseRepositoryHook } from "../../hooks/browseRepositoryHook";
import Error from "../abstractComponents/error";
import ErrorBoundary from "../abstractComponents/errorBoundary";
import { RepositoryObject, RepositoryTree } from "../../backend/types";
import { TreeViewListFilesInRepo } from "./treeViewListFilesInRepo";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { TableViewListFilesInRepo } from "./tableViewListFilesInRepo";
import { FilePreview } from "./filePreview";
import EditAction from "../editAction";
import ActionsOfFiles from "../contextMenu/actionsOfFiles";
import TracebilityReport from "../contextMenu/tracebilityReport";
import NavBarPmxActivity from "./navbarPmxActivity";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Success from "../abstractComponents/success";
import Link from "@mui/material/Link";
import { Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

export const FileExplorer = () => {
  const {
    isCreateFolderWindowOpen,
    setIsCreateFolderWindowOpen,
    isLoadingCreateFolderMutation,
    errorCreateFolderMutation,
    setCurrentSelectedPath,
    setCurrentSelectedVersionId,
    setCurrentSelectedRevision,
    createFolder,
    currentSelectedPath,
    currentSelectedVersionId,
    currentSelectedRevision,
    isActionOfFilesVisible,
    setIsActionOfFilesVisible,
    isTracebilityReportVisible,
    setIsTracebilityReportVisible,
    tableMenuWasOpenOnPath,
    setTableMenuWasOpenOnPath,
    onClosePropertiesModal,
    onCloseTracebilityModal,
    selectedCommentIdFromMenu,
    setSelectedCommentIdFromMenu,
    isEditActionVisible,
    setIsEditActionVisible,
    isProcessingExport,
    setIsProcessingExport,
    openPropertiesModal,
    setIsCopyMoveModalOpenHook,
    isPreselectedRevision,
    setIsPreselectedRevision,
    setCurrentSelectedRepoId,
    currentSelectedRepoId,
    tableViewFiles, // files to be shown in the table view on the right side when selecting a repository/folder in the tree view on the left side
    treeHierarchy, // tree hierarchy for the tree view on the left side
    callRepositoryObjects,
    refetchRepositoryObjects,
    isGetRepositoryObjectsLoading,
    getRepositoryObjectsError,
    foldersAndCount,
    dataRefreshPmxActivity,
    forceRefreshPmxActivity,
    isRefreshPmxActivityLoading,
    errorRefreshPmxActivity
  } = useBrowseRepositoryHook();

  const { isActivityLoading, errorActivity, activityId, activityData } =
    useContext(PmxActivityDetailsContext);

  // Define the argument type for onNodeSelectFunction
  interface NodeSelectParams {
    path: string;
    versionId: string;
    revision: string;
    repoId: string;
    pageNumber?: number;
  }

  // Makes the request to get the repository objects when selecting a file or folder in the tree view (left side)
  // or clicking the table view (right side)
  const onNodeSelectFunction = ({
    path,
    versionId = "",
    revision = "",
    repoId = currentSelectedRepoId,
    pageNumber = 1
  }: NodeSelectParams) => {
    setCurrentSelectedPath(path);
    // Query only for folders
    if (path.endsWith("/")) {
      callRepositoryObjects({
        variables: {
          repoId: String(repoId),
          location: path,
          pageNumber: pageNumber
        }
      });
    }
    setCurrentSelectedVersionId(versionId!);
    setCurrentSelectedRevision(revision!);
    setCurrentSelectedRepoId(repoId);
  };

  const isTreeViewVisibleStorage = () => {
    if (
      localStorage.getItem("isTreeViewVisibleStorage") &&
      localStorage.getItem("isTreeViewVisibleStorage") !== "undefined" &&
      localStorage.getItem("isTreeViewVisibleStorage") !== ""
    ) {
      if (localStorage.getItem("isTreeViewVisibleStorage") === "false") {
        return false;
      } else {
        return true;
      }
    } else {
      localStorage.setItem("isTreeViewVisibleStorage", String(true));
      return true;
    }
  };

  const [isTreeViewVisible, setIsTreeViewVisible] = useState(
    isTreeViewVisibleStorage()
  );

  const onClickToggleTreeViewButton = () => {
    localStorage.setItem(
      "isTreeViewVisibleStorage",
      String(!isTreeViewVisible)
    );
    setIsTreeViewVisible(!isTreeViewVisible);
  };

  const getRepositoryObjectForTraceabilityReport = ():
    | RepositoryObject
    | undefined => {
    return tableViewFiles.find(
      (element: RepositoryObject) => element.name === tableMenuWasOpenOnPath
    );
  };

  const getVersionIdByPathSelected = (): string => {
    return String(
      tableViewFiles.find(
        (element: any) => element.name === tableMenuWasOpenOnPath
      )?.versionId
    );
  };

  return (
    <>
      <NavBarPmxActivity
        refetchRepositoryObjects={refetchRepositoryObjects}
        activeIndex={0}
        onNodeSelect={onNodeSelectFunction}
        selectedCommentIdFromMenu={selectedCommentIdFromMenu}
        setSelectedCommentIdFromMenu={setSelectedCommentIdFromMenu}
      />
      <div className="app_content pmx_app_content">
        {!activityData &&
          !tableViewFiles &&
          (isActivityLoading || isGetRepositoryObjectsLoading) && (
            <div className="center_div">
              <br />
              <Spinner animation="border" className="spinner_color" />
              <p>Loading...</p>
            </div>
          )}
        {activityData && tableViewFiles && (
          <>
            <Container fluid className="browse-pmx-activity-container">
              <Row className="pmx-activity-row-container">
                <Breadcrumbs
                  aria-label="breadcrumb"
                  className="float-start pmx_bradcrumbs"
                  separator={<NavigateNextIcon fontSize="small" />}
                >
                  <Link
                    key={"root"}
                    onClick={() =>
                      onNodeSelectFunction({ path: "/" } as NodeSelectParams)
                    }
                    className="breadcrumb_element"
                  >
                    {activityData.trialNumber}
                  </Link>
                  {currentSelectedPath.split("/").map((name, index) => {
                    if (name === "") return null;
                    const delay = currentSelectedPath.endsWith("/") ? 2 : 1; // last item can be a folder or a file, but should not have a link
                    if (
                      index ===
                      currentSelectedPath.split("/").length - delay
                    ) {
                      return <Typography key={index}>{name}</Typography>;
                    }
                    const path =
                      currentSelectedPath
                        .split("/")
                        .slice(0, index + 1)
                        .join("/") + "/";

                    return (
                      <Link
                        key={index}
                        onClick={() =>
                          onNodeSelectFunction({
                            path: path
                          } as NodeSelectParams)
                        }
                        className="breadcrumb_element"
                      >
                        {name}
                      </Link>
                    );
                  })}
                  {(isLoadingCreateFolderMutation || isProcessingExport) && (
                    <Spinner animation="border" className="spinner_color" />
                  )}
                </Breadcrumbs>
                <br />
                <hr />
                <div
                  className={`${
                    isTreeViewVisible ? "d-block" : "d-none"
                  } w-25 tree-view-column text-break tree_view_column`}
                >
                  {treeHierarchy?.map((repositoryTree: RepositoryTree) => (
                    <ErrorBoundary key={repositoryTree.repository.id}>
                      <TreeViewListFilesInRepo
                        foldersAndCount={foldersAndCount}
                        repositoryTree={repositoryTree}
                        activityData={activityData}
                        onNodeSelect={onNodeSelectFunction}
                        currentSelectedPath={currentSelectedPath}
                        currentSelectedRepoId={currentSelectedRepoId}
                      />
                    </ErrorBoundary>
                  ))}
                </div>
                <div className="align-self-start mt-2 w-auto h-100">
                  <Button
                    onClick={onClickToggleTreeViewButton}
                    className={"close_tree_view_button"}
                    variant="light"
                  >
                    {isTreeViewVisible ? (
                      <ArrowBackIosNewIcon />
                    ) : (
                      <ArrowForwardIosIcon />
                    )}
                  </Button>
                </div>
                <div
                  className={
                    isTreeViewVisible
                      ? "table_view_column flex-grow-1"
                      : "table_view_column_closed flex-grow-1"
                  }
                >
                  {!currentSelectedPath?.endsWith("/") &&
                  currentSelectedPath !== "" ? (
                    <ErrorBoundary>
                      <FilePreview
                        absolutePath={currentSelectedPath}
                        versionId={currentSelectedVersionId}
                        revision={currentSelectedRevision}
                        setTableMenuWasOpenOnPath={setTableMenuWasOpenOnPath}
                        setIsActionOfFilesVisible={setIsActionOfFilesVisible}
                        openPropertiesModal={openPropertiesModal}
                        setIsPreselectedRevision={setIsPreselectedRevision}
                        setIsTracebilityReportVisible={
                          setIsTracebilityReportVisible
                        }
                        repoId={currentSelectedRepoId!}
                      />
                    </ErrorBoundary>
                  ) : (
                    <ErrorBoundary>
                      <TableViewListFilesInRepo
                        areRepoObjectsLoading={isGetRepositoryObjectsLoading}
                        createFolderMutation={createFolder}
                        currentSelectedPath={currentSelectedPath}
                        currentSelectedRepoId={String(
                          currentSelectedRepoId || null
                        )}
                        currentTreeRepositoryObjects={
                          treeHierarchy?.find(
                            (repo: RepositoryTree) =>
                              repo.repository.id ===
                              String(currentSelectedRepoId)
                          )?.repositoryObjects || ([] as RepositoryObject[])
                        }
                        foldersAndCount={foldersAndCount}
                        forceRefreshPmxActivity={forceRefreshPmxActivity}
                        getRepositoryObjects={callRepositoryObjects}
                        isCreateFolderWindowOpen={isCreateFolderWindowOpen}
                        onNodeSelect={onNodeSelectFunction}
                        refetchRepositoryObjects={refetchRepositoryObjects}
                        setCurrentSelectedRepoId={setCurrentSelectedRepoId}
                        setCurrentSelectedVersionId={
                          setCurrentSelectedVersionId
                        }
                        setIsActionOfFilesVisible={setIsActionOfFilesVisible}
                        setIsCopyMoveModalOpenHook={setIsCopyMoveModalOpenHook}
                        setIsEditActionVisible={setIsEditActionVisible}
                        setIsProcessingExport={setIsProcessingExport}
                        setIsTracebilityReportVisible={
                          setIsTracebilityReportVisible
                        }
                        setIsCreateFolderWindowOpen={
                          setIsCreateFolderWindowOpen
                        }
                        setSelectedCommentIdFromMenu={
                          setSelectedCommentIdFromMenu
                        }
                        setTableMenuWasOpenOnPath={setTableMenuWasOpenOnPath}
                        tableMenuWasOpenOnPath={tableMenuWasOpenOnPath}
                        tableViewFiles={tableViewFiles}
                      />
                    </ErrorBoundary>
                  )}
                </div>
              </Row>

              <br />
            </Container>
          </>
        )}
        {isActionOfFilesVisible && treeHierarchy && (
          <ActionsOfFiles
            absolutePath={tableMenuWasOpenOnPath}
            isPreselectedRevision={isPreselectedRevision}
            onClose={onClosePropertiesModal}
            onNodeSelect={onNodeSelectFunction}
            refetchRepositoryObject={refetchRepositoryObjects}
            repoId={currentSelectedRepoId}
            setIsPreselectedRevision={setIsPreselectedRevision}
            versionId={getVersionIdByPathSelected()}
          />
        )}
        {isEditActionVisible && treeHierarchy && (
          <ErrorBoundary>
            <EditAction
              notEditableAction={false}
              onClose={() => {
                setIsEditActionVisible(false);
              }}
              openDirectly={true}
              refetch={refetchRepositoryObjects}
              versionId={getVersionIdByPathSelected()}
            />
          </ErrorBoundary>
        )}
        {isTracebilityReportVisible && treeHierarchy && (
          <ErrorBoundary>
            <TracebilityReport
              activityId={Number(activityData.id)}
              currentFile={getRepositoryObjectForTraceabilityReport()}
              onNodeSelect={onNodeSelectFunction}
              onClose={onCloseTracebilityModal}
            />
          </ErrorBoundary>
        )}

        {(getRepositoryObjectsError ||
          errorActivity ||
          errorCreateFolderMutation ||
          errorRefreshPmxActivity) && (
          <Error
            error={
              (getRepositoryObjectsError &&
              String(getRepositoryObjectsError)
                .toLowerCase()
                .includes("failed to fetch")
                ? "The system is still processing new files and will not respond during that time. Please try again in a few minutes."
                : getRepositoryObjectsError) ||
              errorActivity ||
              errorCreateFolderMutation ||
              errorRefreshPmxActivity
            }
          />
        )}

        {dataRefreshPmxActivity && (
          <Success
            message={
              dataRefreshPmxActivity
                ? "Force refresh will finish in a few minutes"
                : "There is a job already running. Please wait for it to finish"
            }
          />
        )}
      </div>
    </>
  );
};
